import type { ReactNode } from 'react';
import { clsx } from '../../../../ds/clsx';
import type { CompanyBilling } from '../api';
import styles from './billing-ui.module.css';

export interface BillingModeCardProps {
  /** The company-mode answer of `GET /settings/billing`. */
  billing: CompanyBilling;
  /**
   * `settings`: the 760-px card of Einstellungen 18 (facts „Arbeitsplatz" / „Zugewiesen von", footer line);
   * `wizard`: the variant card of Onboarding Nutzer 10 (eyebrow, one workplace line, an action).
   */
  variant?: 'settings' | 'wizard' | undefined;
  /** Wizard variant: eyebrow above the icon, e.g. „Variante · über die Firma abgerechnet". */
  eyebrow?: string | undefined;
  /** Replaces the API title (wizard: „Kein Zahlungsmittel nötig"). */
  title?: string | undefined;
  /** Replaces the API text. */
  text?: string | undefined;
  /** Wizard variant: the button below the workplace („Weiter zur Sicherheit"). */
  action?: ReactNode | undefined;
  className?: string | undefined;
}

/** Name part of „M. Osterloh · 12.09.2026". */
function assignerName(assignedBy: string | null): string | null {
  if (!assignedBy) return null;
  return assignedBy.split(' · ')[0] ?? assignedBy;
}

/** „Ihr Zugang wird über die Organisation abgerechnet." — the company-paid seat (no payment data). */
export function BillingModeCard({
  billing,
  variant = 'settings',
  eyebrow,
  title,
  text,
  action,
  className,
}: BillingModeCardProps) {
  if (variant === 'wizard') {
    const assigner = assignerName(billing.assignedBy);
    return (
      <section
        className={clsx(styles.mode, styles.modeWizard, className)}
        aria-label={title ?? billing.title}
      >
        {eyebrow ? <span className={styles.modeEyebrow}>{eyebrow}</span> : null}
        <span className={styles.modeIcon} aria-hidden="true">
          €
        </span>
        <h3 className={styles.modeTitle}>{title ?? billing.title}</h3>
        <p className={styles.modeText}>{text ?? billing.text}</p>
        <div className={styles.modeWorkplace}>
          <span className={styles.modeWorkplaceLabel}>Arbeitsplatz</span>
          <span className={styles.modeWorkplaceValue}>
            {assigner ? `${billing.workplace} · zugewiesen von ${assigner}` : billing.workplace}
          </span>
        </div>
        {action}
      </section>
    );
  }
  return (
    <section className={clsx(styles.mode, className)} aria-label={title ?? billing.title}>
      <span className={styles.modeIcon} aria-hidden="true">
        €
      </span>
      <h3 className={styles.modeTitle}>{title ?? billing.title}</h3>
      <p className={styles.modeText}>{text ?? billing.text}</p>
      <div className={styles.modeFacts}>
        <div>
          <div className={styles.modeFactLabel}>Arbeitsplatz</div>
          <div className={styles.modeFactValue}>{billing.workplace}</div>
        </div>
        {billing.assignedBy ? (
          <div>
            <div className={styles.modeFactLabel}>Zugewiesen von</div>
            <div className={styles.modeFactValue}>{billing.assignedBy}</div>
          </div>
        ) : null}
      </div>
      <span className={styles.modeFooter}>{billing.footer}</span>
      {action}
    </section>
  );
}
